<template>
  <div class="page-outer">

    <!-- 固定頂部 Header -->
    <div class="page-header page-header-rich-menu-editor header-with-preview">
      <div class="header-left">
        <h1 class="page-title">{{ isEditMode ? '編輯圖文選單' : '新增圖文選單' }}</h1>
        <div v-if="isEditMode" class="status-row">
          <span :class="['status-badge', groupStatus, { 'line-default': isLineDefaultFlag }]">
            <i :class="statusIcon"></i>
            {{ statusText }}
          </span>
          <span v-if="isBaseMenu" class="base-menu-badge">
            <i class="ri-home-heart-fill"></i> 基本選單
          </span>
        </div>
      </div>
      <div class="header-actions">
        <button class="btn btn-secondary" @click="handleCancel">返回</button>
        <div class="actions-dropdown-wrap" ref="actionsWrap">
          <button class="btn btn-actions" @click="actionsMenuOpen = !actionsMenuOpen">
            <i class="ri-more-fill"></i>
            動作
            <i :class="actionsMenuOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'" style="margin-left:2px;"></i>
          </button>
          <div v-show="actionsMenuOpen" class="actions-dropdown-menu">
            <button
              class="actions-menu-item"
              @click="handleSaveDraft(); actionsMenuOpen = false"
              :disabled="!!saving || activating || applying"
            >
              <i :class="saving === 'draft' ? 'ri-loader-4-line spin' : 'ri-draft-line'"></i>
              {{ saving === 'draft' ? '儲存中...' : '存為草稿' }}
            </button>
            <button
              v-if="!(isEditMode && isLineDefaultFlag)"
              class="actions-menu-item item-activate"
              @click="handleActivate(); actionsMenuOpen = false"
              :disabled="!!saving || activating || applying"
            >
              <i :class="activating ? 'ri-loader-4-line spin' : 'ri-checkbox-circle-line'"></i>
              {{ activating ? '建立中...' : '儲存建立選單' }}
            </button>
            <button
              v-if="isEditMode && groupStatus === 'active'"
              class="actions-menu-item item-apply"
              @click="handleApplyAsDefault(); actionsMenuOpen = false"
              :disabled="!!saving || activating || applying || settingBase"
            >
              <i :class="applying ? 'ri-loader-4-line spin' : 'ri-broadcast-line'"></i>
              {{ applying ? '套用中...' : '儲存並立即套用' }}
            </button>
            <button
              v-if="isEditMode && groupStatus === 'active'"
              :class="['actions-menu-item', isBaseMenu ? 'item-base-active' : 'item-base']"
              @click="handleToggleBaseMenu(); actionsMenuOpen = false"
              :disabled="!!saving || activating || applying || settingBase"
            >
              <i :class="settingBase ? 'ri-loader-4-line spin' : (isBaseMenu ? 'ri-home-heart-fill' : 'ri-home-heart-line')"></i>
              {{ settingBase ? '設定中...' : (isBaseMenu ? '取消基本選單' : '設為基本選單') }}
            </button>
          </div>
        </div>
      </div>
    </div>

  <div class="page-container">

    <!-- 桌機版浮動預覽 -->
    <div class="preview-sticky">
      <div class="preview-title">
        <i class="ri-smartphone-line"></i>
        <span>圖文選單預覽</span>
      </div>
      <RichMenuPreview
        :tabs="form.tabs"
        :active-tab="activeTabIndex"
        :tab-bar-size="form.tabBarSize"
        :menu-open-by-default="form.menuOpenByDefault === true"
        :simulated-messages="simulatedMessages"
        :click-counts="analyticsSummary.buttons"
        @tab-change="activeTabIndex = $event"
        @cell-click="handleSimCellClick"
        @clear-sim="simulatedMessages = []"
      />
    </div>


    <!-- 手機版預覽開關 -->
    <div class="mobile-preview-toggle-bar">
      <button class="mobile-preview-toggle-btn" @click="mobilePreviewOpen = !mobilePreviewOpen">
        <i :class="mobilePreviewOpen ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
        {{ mobilePreviewOpen ? '關閉預覽' : '開啟圖文選單預覽' }}
      </button>
    </div>
    <div v-if="mobilePreviewOpen" class="mobile-preview-drawer">
      <div class="mobile-preview-header">
        <span><i class="ri-smartphone-line"></i> 圖文選單預覽</span>
        <button class="mobile-preview-close" @click="mobilePreviewOpen = false">
          <i class="ri-close-line"></i>
        </button>
      </div>
      <div class="mobile-preview-body">
        <RichMenuPreview
          :tabs="form.tabs"
          :active-tab="activeTabIndex"
          :tab-bar-size="form.tabBarSize"
          :menu-open-by-default="form.menuOpenByDefault === true"
          :simulated-messages="simulatedMessages"
          :click-counts="analyticsSummary.buttons"
          @tab-change="activeTabIndex = $event"
          @cell-click="handleSimCellClick"
          @clear-sim="simulatedMessages = []"
        />
      </div>
    </div>

    <div v-if="isEditMode && trackingEnabled" class="tracking-change-warning">
      <i class="ri-error-warning-line"></i>
      <div>
        <strong>此選單已啟用點擊追蹤</strong>
        <p>
          變更 Tab 數量、版型、高度，或按鈕名稱、動作類型、動作內容時，
          統計會在<strong>下次套用後</strong>建立新版本；目前版本資料仍可於點擊分析查詢。
        </p>
      </div>
    </div>

    <!-- ─── 頁籤導航 ─────────────────────────────────────── -->
    <div class="editor-tab-nav">
      <button
        :class="['editor-tab-btn', { active: activeEditorTab === 'basic' }]"
        @click="activeEditorTab = 'basic'"
      >
        <i class="ri-settings-3-line"></i> 基本設定
      </button>
      <button
        :class="['editor-tab-btn', { active: activeEditorTab === 'tabs' }]"
        @click="activeEditorTab = 'tabs'"
      >
        <i class="ri-layout-column-line"></i> 選單 Tab 設定
      </button>
      <button
        :class="['editor-tab-btn', { active: activeEditorTab === 'test' }]"
        @click="activeEditorTab = 'test'"
      >
        <i class="ri-user-star-line"></i> 指定測試名單
      </button>
      <button
        v-if="isEditMode"
        :class="['editor-tab-btn', { active: activeEditorTab === 'analytics' }]"
        @click="activeEditorTab = 'analytics'"
      >
        <i class="ri-bar-chart-box-line"></i> 點擊分析
      </button>
    </div>

    <div class="editor-layout">

      <!-- ─── 基本設定 ─────────────────────────────────────── -->
      <div v-show="activeEditorTab === 'basic'" class="editor-section">
        <div class="section-header-row">
          <i class="ri-settings-3-line section-icon"></i>
          <h3>基本設定</h3>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label class="form-label required-label">選單名稱</label>
            <input v-model="form.name" type="text" class="form-control" placeholder="例如：主選單 2024" />
          </div>
          <div class="form-group">
            <label class="form-label required-label">LINE 官方帳號</label>
            <select v-model="form.lineConfigId" class="form-select">
              <option value="">請選擇帳號</option>
              <option v-for="c in lineConfigs" :key="c.id" :value="c.id">{{ c.configName }}</option>
            </select>
          </div>
        </div>

        <div class="form-group menu-open-default-group">
          <label class="form-label">圖文選單預設顯示</label>
          <p class="menu-open-hint">
            好友開啟與官方帳號的聊天室時，圖文選單是否預設展開。
            變更後須重新「儲存並啟用」才會同步到 LINE。
          </p>
          <div class="menu-open-toggle">
            <button
              type="button"
              :class="['menu-open-opt', { active: !form.menuOpenByDefault }]"
              @click="form.menuOpenByDefault = false"
            >
              <i class="ri-menu-unfold-line"></i>
              <span class="opt-title">預設收起</span>
              <span class="opt-desc">僅顯示選單列，需點擊才展開</span>
            </button>
            <button
              type="button"
              :class="['menu-open-opt', { active: form.menuOpenByDefault }]"
              @click="form.menuOpenByDefault = true"
            >
              <i class="ri-menu-line"></i>
              <span class="opt-title">預設顯示</span>
              <span class="opt-desc">進入聊天室時選單為展開狀態</span>
            </button>
          </div>
        </div>
      </div>

      <!-- ─── Tab 設定 ──────────────────────────────────────── -->
      <div v-show="activeEditorTab === 'tabs'" class="editor-section">
        <div class="section-header-row">
          <i class="ri-layout-column-line section-icon"></i>
          <h3>Tab 設定</h3>
          <span class="section-hint">最多 4 個 Tab</span>
        </div>

        <!-- Tab 列高度（2+ Tab 才顯示） -->
        <div v-if="form.tabs.length > 1" class="form-group tab-bar-size-group">
          <label class="form-label">Tab 切換列高度</label>
          <div class="tab-bar-size-toggle">
            <button
              v-for="opt in tabBarSizeOptions"
              :key="opt.value"
              :class="['tab-size-btn', { active: form.tabBarSize === opt.value }]"
              @click="form.tabBarSize = opt.value"
            >
              <div class="tab-size-visual">
                <div class="tab-size-bar" :style="{ height: opt.previewHeight }"></div>
                <div class="tab-size-body"></div>
              </div>
              <div class="tab-size-info">
                <strong>{{ opt.label }}</strong>
                <span>Tab 列 {{ tabBarPx(opt.value) }}px</span>
              </div>
            </button>
          </div>
        </div>

        <!-- 圖片切法說明（永遠顯示） -->
        <div class="image-cut-guide">
          <i class="ri-scissors-line"></i>
          <span v-if="form.tabs.length > 1">
            背景圖建議切法：
            上方 <strong>{{ tabBarPx(form.tabBarSize) }}px</strong>（Tab 切換區）
            + 下方 <strong>{{ buttonAreaPx(form.tabBarSize) }}px</strong>（按鈕區）
            = 總高 <strong>{{ totalHeightPx }}px</strong>
          </span>
          <span v-else>
            背景圖尺寸：寬 <strong>2500px</strong> × 高 <strong>{{ totalHeightPx }}px</strong>，整張圖作為按鈕底圖
          </span>
        </div>

        <!-- Tab 頁籤卡片（tab-bar + tab-editor 合為一個卡片） -->
        <div class="tab-panel">
          <!-- 頁籤列 -->
          <div class="tab-header">
            <div class="tab-header-tabs">
              <button
                v-for="(tab, idx) in form.tabs"
                :key="idx"
                :class="['tab-btn', { active: activeTabIndex === idx }]"
                @click="activeTabIndex = idx"
              >
                {{ tab.tabName || `Tab ${idx + 1}` }}
                <span
                  v-if="form.tabs.length > 1"
                  class="tab-remove"
                  @click.stop="removeTab(idx)"
                ><i class="ri-close-line"></i></span>
              </button>
              <button
                v-if="form.tabs.length < 4"
                class="btn-add-tab"
                @click="addTab"
              >
                <i class="ri-add-line"></i> 新增 Tab
              </button>
            </div>
          </div>

        <!-- 當前 Tab 設定 -->
        <div v-if="currentTab" class="tab-editor">

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Tab 名稱</label>
              <input
                v-model="currentTab.tabName"
                type="text"
                class="form-control"
                placeholder="Tab 1"
                @focus="($event) => $event.target.select()"
              />
            </div>
            <div class="form-group">
              <label class="form-label">選單欄文字（最多 14 字）</label>
              <input
                v-model="currentTab.chatBarText"
                type="text"
                class="form-control"
                placeholder="查看更多"
                maxlength="14"
                @focus="($event) => $event.target.select()"
              />
            </div>
          </div>

          <!-- 高度選擇（僅單 Tab 時可選半高） -->
          <div class="form-group">
            <label class="form-label required-label">選單高度</label>
            <div class="height-toggle">
              <button
                :class="['height-btn', { active: currentTab.heightType !== 'half' }]"
                @click="selectHeight('full')"
              >
                <div class="height-preview height-full">
                  <div class="height-fill"></div>
                </div>
                <div class="height-info">
                  <strong>全高</strong>
                  <span>2500 × 1686 px</span>
                  <span class="height-layouts">最多 8 格</span>
                </div>
              </button>
              <button
                :class="['height-btn', { active: currentTab.heightType === 'half' }, { disabled: form.tabs.length > 1 }]"
                @click="form.tabs.length === 1 && selectHeight('half')"
                :title="form.tabs.length > 1 ? '半高僅支援單 Tab 模式' : ''"
              >
                <div class="height-preview height-half">
                  <div class="height-fill"></div>
                </div>
                <div class="height-info">
                  <div class="height-info-title">
                    <strong>半高</strong>
                    <span class="height-single-only">僅適用於單頁選單</span>
                  </div>
                  <span>2500 × 843 px</span>
                  <span class="height-layouts">最多 4 格</span>
                </div>
                <span v-if="form.tabs.length > 1" class="height-lock">
                  <i class="ri-lock-line"></i> 多 Tab 不可用
                </span>
              </button>
            </div>
          </div>

          <!-- 版型選擇 -->
          <div class="form-group">
            <label class="form-label required-label">版型（按鈕數量）</label>
            <div class="layout-picker">
              <button
                v-for="layout in currentLayoutOptions"
                :key="layout.value"
                :class="['layout-btn', { active: currentTab.layoutType === layout.value }]"
                @click="selectLayout(layout.value)"
                :title="layout.label"
              >
                <div
                  class="layout-preview"
                  :class="[`layout-${layout.value}`, { 'half-mode': currentTab.heightType === 'half' }]"
                >
                  <div v-for="n in parseInt(layout.value)" :key="n" class="layout-cell"></div>
                </div>
                <span class="layout-label">{{ layout.label }}</span>
              </button>
            </div>
          </div>

          <!-- 背景圖上傳 -->
          <div class="form-group">
            <label class="form-label">背景圖片</label>
            <p class="form-hint">
              建議尺寸：{{ currentTab.heightType === 'half' ? '2500 × 843' : '2500 × 1686' }} px（JPG/PNG），圖片只需上傳一次，修改版型不需重傳
            </p>
            <ImageUploader
              v-model="currentTab.imageUrl"
              :upload-fn="uploadImage"
              shape="card"
              :aspect-ratio="currentTab.heightType === 'half' ? '2500 / 843' : '2500 / 1686'"
              :crop-width="2500"
              :crop-height="currentTab.heightType === 'half' ? 843 : 1686"
              :max-size-mb="1"
              accept="image/jpeg,image/png"
              :hint="currentTab.heightType === 'half' ? '建議 2500×843px，僅支援 JPG/PNG' : '建議 2500×1686px，僅支援 JPG/PNG'"
              size-hint="最大 1MB，尺寸不符時自動裁切"
            />
            <div class="ai-generate-row">
              <button type="button" class="btn btn-ai-generate" @click="aiGenerateOpen = true">
                <i class="ri-magic-line"></i> AI 生成背景圖
              </button>
              <span class="ai-generate-hint">依活動主題與企業色自動生圖，依實際 token 用量計費</span>
            </div>
            <RichMenuAiGenerateModal
              :open="aiGenerateOpen"
              :height-type="currentTab.heightType"
              :layout-type="currentTab.layoutType"
              :tab-name="currentTab.tabName"
              :tab-names="form.tabs.map(tab => tab.tabName || '')"
              :tab-count="form.tabs.length"
              :tab-bar-size="form.tabBarSize"
              :area-hints="currentTab.areas"
              @close="aiGenerateOpen = false"
              @generated="onAiImageGenerated"
            />
          </div>

          <!-- 按鈕區域設定 -->
          <div class="form-group">
            <label class="form-label">按鈕設定</label>
            <div class="areas-list">
              <div
                v-for="(area, aIdx) in currentTab.areas"
                :key="aIdx"
                class="area-item"
              >
                <div class="area-header">
                  <span class="area-num">按鈕 {{ aIdx + 1 }}</span>
                </div>
                <div class="area-fields">
                  <input
                    v-model="area.label"
                    type="text"
                    class="form-control"
                    placeholder="選填；留空時存檔會自動命名為 1-1、1-2…"
                    @focus="($event) => $event.target.select()"
                  />
                  <!-- switchTab 獨立處理，其餘交給 LineActionField -->
                  <template v-if="area.actionType === 'switchTab'">
                    <select v-model="area.actionType" class="form-select">
                      <option value="uri">開啟網址</option>
                      <option value="message">發送文字訊息</option>
                      <option value="postback">Postback</option>
                      <option v-if="form.tabs.length > 1" value="switchTab">切換 Tab</option>
                    </select>
                    <select
                      v-model="area.actionData"
                      class="form-select"
                      :class="{ 'select-error': area.actionData === '' || area.actionData === null || area.actionData === undefined }"
                    >
                      <option value="" disabled>請選擇要切換的 Tab</option>
                      <option
                        v-for="(tab, tIdx) in form.tabs"
                        :key="tIdx"
                        :value="String(tIdx)"
                        :disabled="tIdx === activeTabIndex"
                      >{{ tab.tabName || `Tab ${tIdx + 1}` }}</option>
                    </select>
                    <span
                      v-if="area.actionData === '' || area.actionData === null || area.actionData === undefined"
                      class="field-error-inline"
                    ><i class="ri-error-warning-line"></i> 請選擇要切換的 Tab</span>
                  </template>
                  <template v-else>
                    <LineActionField
                      :model-value="areaToAction(area)"
                      :show-switch-tab-option="form.tabs.length > 1"
                      invite-official-account
                      :official-account-basic-id="selectedLineBasicId"
                      :official-account-name="selectedLineAccountName"
                      @update:model-value="onActionUpdate(area, $event)"
                    />
                  </template>
                </div>
              </div>
            </div>
          </div>

        </div>
        </div><!-- /tab-panel -->
      </div>

      <!-- ─── 顯示模式 ─────────────────────────────────────── -->
      <div v-show="activeEditorTab === 'basic'" class="editor-section">
        <div class="section-header-row">
          <i class="ri-time-line section-icon"></i>
          <h3>顯示模式</h3>
          <span v-if="isEditMode" class="mode-locked-badge">
            <i class="ri-lock-line"></i> 建立後無法變更
          </span>
        </div>

        <!-- 新增 / 編輯時：統一唯讀顯示模式，不允許自行切換 -->
        <div class="mode-readonly">
          <div :class="['mode-readonly-badge', form.displayMode]">
            <i :class="form.displayMode === 'schedule' ? 'ri-calendar-line' : 'ri-vip-crown-line'"></i>
            <span>{{ form.displayMode === 'schedule' ? '排程模式' : '會員等級模式' }}</span>
          </div>
          <p class="mode-readonly-desc">
            {{ form.displayMode === 'schedule'
              ? '設定顯示起訖時間，系統每小時自動啟用與關閉。'
              : '依照會員等級決定顯示對象，部署時系統自動連結符合等級的好友。' }}
          </p>
          <p v-if="!isEditMode" class="mode-change-hint">
            <i class="ri-settings-3-line"></i>
            如需切換模式，請至
            <router-link to="/client/settings?tab=line" target="_blank">系統參數 › LINE 設定</router-link>
            變更後再新增選單
          </p>
        </div>

        <!-- 排程設定 -->
        <div v-if="form.displayMode === 'schedule'" class="mode-settings">
          <div class="form-row">
            <div class="form-group">
              <label class="form-label">開始時間</label>
              <div class="hour-picker">
                <input
                  type="date"
                  class="form-control"
                  v-model="scheduleParts.startDate"
                  @change="_syncFormFromParts"
                />
                <select
                  class="form-control hour-select"
                  v-model="scheduleParts.startHour"
                  @change="_syncFormFromParts"
                >
                  <option v-for="h in 24" :key="h-1" :value="String(h-1).padStart(2,'0')">
                    {{ String(h-1).padStart(2,'0') }} 時
                  </option>
                </select>
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">結束時間（留空表示無限期）</label>
              <div class="hour-picker">
                <input
                  type="date"
                  class="form-control"
                  v-model="scheduleParts.endDate"
                  @change="_syncFormFromParts"
                />
                <select
                  class="form-control hour-select"
                  v-model="scheduleParts.endHour"
                  @change="_syncFormFromParts"
                >
                  <option v-for="h in 24" :key="h-1" :value="String(h-1).padStart(2,'0')">
                    {{ String(h-1).padStart(2,'0') }} 時
                  </option>
                </select>
              </div>
            </div>
          </div>
          <!-- 時間判斷說明 -->
          <div class="schedule-time-guide">
            <div class="schedule-time-guide__title">
              <i class="ri-time-line"></i> 時間判斷說明
            </div>
            <div class="schedule-time-guide__body">
              <div class="schedule-time-guide__row">
                <span class="schedule-time-guide__label">開始時間</span>
                <span>
                  設定的時間點（含）開始生效。系統固定於<strong>每小時整點（:00）</strong>執行排程，上架通常在整點後數分鐘內完成。<br>
                  <span class="schedule-time-guide__example">
                    範例：設定 4 月 20 日 <strong>14:00</strong> → 4/20 14:00 整點排程啟動，數分鐘內完成上架，好友即可看到此選單。
                  </span>
                </span>
              </div>
              <div class="schedule-time-guide__row">
                <span class="schedule-time-guide__label">結束時間</span>
                <span>
                  設定的時間點（含）開始下架。亦即 <strong>設定時間的前一整點為最後有效時段</strong>。<br>
                  <span class="schedule-time-guide__example">
                    範例：設定 4 月 25 日 <strong>14:00</strong> → 選單有效至 4/25 13:xx，4/25 14:00 整點排程啟動後數分鐘內完成下架。
                  </span>
                </span>
              </div>
              <div class="schedule-time-guide__row schedule-time-guide__row--tip">
                <i class="ri-lightbulb-line"></i>
                <span>若希望選單顯示到 4/25 結束全天，結束時間請設為 <strong>4 月 26 日 00:00</strong>。</span>
              </div>
            </div>
          </div>
          <p class="hint-box hint-info">
            <i class="ri-information-line"></i>
            <span>
              先按「儲存建立選單」將選單建立至 LINE。<br>
              <strong>有設定日期起迄</strong>：系統每小時自動檢查，開始時間到自動上架（設為 LINE 預設）；結束時間到自動下架。<br>
              <strong>未設定日期</strong>：不會自動上下架，請手動按「立即套用」。
            </span>
          </p>
          <p v-if="isEditMode && groupStatus === 'active'" class="hint-box hint-success">
            <i class="ri-checkbox-circle-line"></i>
            此選單已啟用（LINE 結構已建立），排程器將在開始時間自動套用為 LINE 預設選單。也可點擊「立即套用」手動立即生效。
          </p>
        </div>

        <!-- 會員等級設定 -->
        <div v-if="form.displayMode === 'member'" class="mode-settings">
          <div class="form-group">
            <label class="form-label">限定會員等級（複選，不選則為非會員選單）</label>
            <div v-if="memberTiers.length === 0" class="hint-box hint-warning">
              <i class="ri-alert-line"></i> 尚未建立任何會員等級
            </div>
            <div v-else class="tier-checkboxes">
              <label
                v-for="tier in memberTiers"
                :key="tier.id"
                class="tier-checkbox"
                :class="{
                  checked: form.tierIds.includes(tier.id),
                  disabled: conflictTierIds.has(String(tier.id))
                }"
                :title="conflictTierIds.has(String(tier.id)) ? `已被「${tierUsage[String(tier.id)]?.groupName}」使用，無法選取` : ''"
              >
                <input
                  type="checkbox"
                  :value="tier.id"
                  v-model="form.tierIds"
                  :disabled="conflictTierIds.has(String(tier.id))"
                />
                <span class="tier-name">{{ tier.tierName }}</span>
                <span v-if="conflictTierIds.has(String(tier.id))" class="tier-conflict-tag">
                  <i class="ri-lock-line"></i>
                  已被「{{ tierUsage[String(tier.id)]?.groupName }}」使用
                </span>
              </label>
            </div>
          </div>

          <p v-if="form.tierIds.length === 0" class="hint-box hint-warning">
            <i class="ri-user-unfollow-line"></i>
            未選擇任何等級，此選單將作為<strong>非會員選單</strong>，顯示給尚未成為會員或不符合任何等級的 LINE 好友。
          </p>
          <p v-else class="hint-box hint-info">
            <i class="ri-information-line"></i>
            按「儲存建立選單」建立 LINE 結構後，再按「立即套用」連結到所有符合等級的 LINE 好友。
          </p>
        </div>
      </div>

      <!-- ─── 測試好友 ─────────────────────────────────────── -->
      <div v-show="activeEditorTab === 'test'" class="editor-section">
        <div class="section-header-row">
          <i class="ri-user-star-line section-icon"></i>
          <h3>測試好友指派</h3>
          <span class="section-hint">啟用後可立即在這些好友的 LINE 看到效果</span>
        </div>

        <div v-if="!isEditMode" class="hint-box hint-info">
          <i class="ri-information-line"></i> 請先儲存選單後，再指派測試好友。
        </div>
        <template v-else>
          <!-- 搜尋列 -->
          <div class="friend-search-row">
            <div class="friend-search-input line-friend-search">
              <input
                type="text"
                class="form-control"
                placeholder="搜尋 LINE 顯示名稱或 User ID…"
                v-model="lineFriendQuery"
                @input="searchLineFriends"
                autocomplete="off"
              />
              <div v-if="lineFriendResults.length > 0" class="line-friend-dropdown">
                <div
                  v-for="f in lineFriendResults"
                  :key="f.lineUserId"
                  class="line-friend-option"
                  @click="selectLineFriend(f)"
                >
                  <img v-if="f.pictureUrl" :src="f.pictureUrl" class="lf-avatar" />
                  <i v-else class="ri-user-3-line lf-avatar-icon"></i>
                  <span class="lf-name">{{ f.displayName }}</span>
                  <span class="lf-uid">{{ f.lineUserId }}</span>
                </div>
              </div>
            </div>
            <button
              class="btn btn-secondary btn-add-friend"
              @click="addFriendDirect"
              :disabled="!selectedLineFriend"
            >
              <i class="ri-user-add-line"></i> 加入
            </button>
          </div>
        </template>

        <div v-if="friends.length > 0" class="friends-list">
          <div v-for="friend in friends" :key="friend.id" class="friend-item">
            <i class="ri-user-line"></i>
            <div class="friend-info">
              <span class="friend-name">{{ friend.memberName || friend.lineUserId }}</span>
              <span class="friend-line-id">{{ friend.lineUserId }}</span>
            </div>
            <span v-if="groupStatus === 'active'" class="friend-linked-badge">
              <i class="ri-link"></i> 已連結
            </span>
            <span v-else class="friend-pending-badge">
              <i class="ri-time-line"></i> 待部署
            </span>
            <button class="btn-icon delete-btn" @click="removeFriend(friend)">
              <i class="ri-delete-bin-line"></i>
            </button>
          </div>
        </div>
        <p v-else-if="isEditMode" class="empty-friends">尚未指派測試好友</p>
      </div>

      <div v-if="isEditMode" v-show="activeEditorTab === 'analytics'" class="editor-section">
        <div class="section-header-row">
          <i class="ri-bar-chart-box-line section-icon"></i>
          <h3>點擊分析</h3>
          <span class="section-hint">依統計版本查詢按鈕點擊明細</span>
        </div>
        <RichMenuAnalyticsPanel
          :menu-id="$route.params.id"
          :initial-analytics="analyticsSummary"
          @summary-loaded="handleAnalyticsSummary"
        />
      </div>

    </div><!-- end editor-layout -->
    </div><!-- end page-container -->
  </div><!-- end page-outer -->
</template>

<script>
import apiClient from '@/api/axios.js';
import { lineRichMenuService } from '@/api/modules/lineRichMenu.js';
import lineConfigApi from '@/api/modules/lineConfig.js';
import { clientSettingsService, NAMESPACES } from '@/api/modules/clientSettings.js';
import { postbackRuleService } from '@/api/modules/postbackRules.js';
import { lineMsgTemplateService } from '@/api/modules/lineMsgTemplates.js';
import { autoReplyService } from '@/api/modules/autoReply.js';
import Swal from 'sweetalert2';
import { showSuccess, showError, showConfirm } from '@/utils/swal.js';
import ImageUploader from '@/components/ImageUploader.vue';
import RichMenuPreview from './RichMenuPreview.vue';
import RichMenuAiGenerateModal from './RichMenuAiGenerateModal.vue';
import RichMenuAnalyticsPanel from './RichMenuAnalyticsPanel.vue';
import LineActionField from '@/components/LineActionField.vue';
import { actionValueFromUri, buildOfficialAccountInviteUri } from '@/utils/lineActionValue.js';

const DEFAULT_TAB = () => ({
  tabIndex: 0,
  tabName: 'Tab 1',
  chatBarText: '查看更多',
  heightType: 'full',  // 'full'(2500×1686) | 'half'(2500×843)，只有單 Tab 才能選 half
  layoutType: '6',
  imageUrl: '',
  areas: Array.from({ length: 6 }, () => ({ label: '', actionType: 'uri', actionData: '' }))
});

export default {
  name: 'LineRichMenuEditor',
  components: {
    ImageUploader,
    RichMenuPreview,
    RichMenuAiGenerateModal,
    RichMenuAnalyticsPanel,
    LineActionField
  },
  data() {
    return {
      isEditMode: false,
      saving: false,    // false | 'draft' | 'publish'
      activating: false,
      applying: false,
      settingBase: false,
      loading: false,
      isBaseMenu: false,
      isLineDefaultFlag: false,
      lineConfigs: [],
      memberTiers: [],
      postbackRules: [],
      autoReplyRules: [],
      autoReplyRulesLoaded: false,
      tierUsage: {},   // { tierId: { groupId, groupName } }
      groupStatus: '',  // 'draft' | 'active'
      friends: [],
      lineFriendQuery: '',
      lineFriendResults: [],
      selectedLineFriend: null,
      lineFriendSearchTimer: null,
      activeTabIndex: 0,
      activeEditorTab: 'basic',
      mobilePreviewOpen: false,
      actionsMenuOpen: false,
      trackingEnabled: false,
      originalTrackingStructure: '',
      analyticsSummary: {
        trackingEnabled: false,
        totalClicks: 0,
        totalUniqueClicks: 0,
        buttons: []
      },
      form: {
        name: '',
        lineConfigId: '',
        displayMode: 'schedule',
        startDate: '',
        endDate: '',
        deployEnabled: false,  // 預設不啟用自動排程，需使用者明確開啟
        tierIds: [],
        tabBarSize: 'wide',   // 'slim'(細版 216px) | 'wide'(粗版 306px)，多 Tab 時有效
        menuOpenByDefault: true, // LINE Rich Menu selected：開啟聊天時是否預設展開（預設為顯示）
        tabs: [{ ...DEFAULT_TAB() }]
      },
      scheduleParts: {
        startDate: '', startHour: '00',
        endDate:   '', endHour:   '00',
      },
      simulatedMessages: [],
      aiGenerateOpen: false,
      layoutOptions: [
        { value: '1', label: '1 格' },
        { value: '2', label: '2 格' },
        { value: '3', label: '3 格' },
        { value: '4', label: '4 格' },
        { value: '6', label: '6 格' },
        { value: '8', label: '8 格' }
      ],
      tabBarSizeOptions: [
        { value: 'slim', label: '細版', previewHeight: '6px' },
        { value: 'wide', label: '粗版', previewHeight: '12px' }
      ]
    };
  },
  computed: {
    statusText() {
      if (this.groupStatus === 'active' && this.isLineDefaultFlag) return '目前顯示';
      return { draft: '草稿', active: '已建立', expired: '已到期' }[this.groupStatus] || this.groupStatus;
    },
    statusIcon() {
      if (this.groupStatus === 'active' && this.isLineDefaultFlag) return 'ri-broadcast-fill';
      return {
        draft: 'ri-draft-line',
        active: 'ri-checkbox-circle-line',
        expired: 'ri-time-line',
      }[this.groupStatus] || 'ri-question-line';
    },
    currentTab() {
      return this.form.tabs[this.activeTabIndex] || null;
    },
    // 依高度模式過濾可用版型
    currentLayoutOptions() {
      const all = this.layoutOptions;
      if (this.currentTab?.heightType === 'half') {
        return all.filter(l => parseInt(l.value) <= 4);
      }
      return all;
    },
    // 選單總高（px）
    totalHeightPx() {
      return this.currentTab?.heightType === 'half' ? 843 : 1686;
    },
    // 有衝突的 tierId 集合（已被其他選單使用，不可再選）
    conflictTierIds() {
      return new Set(Object.keys(this.tierUsage));
    },
    selectedLineConfig() {
      return this.lineConfigs.find(c => c.id === this.form.lineConfigId) || null;
    },
    selectedLineBasicId() {
      const config = this.selectedLineConfig;
      return config?.messagingApi?.botBasicId || config?.botBasicId || '';
    },
    selectedLineAccountName() {
      return this.selectedLineConfig?.botDisplayName || '';
    }
  },
  watch: {
    selectedLineBasicId() {
      this._syncOfficialAccountInvites();
    },
    selectedLineAccountName() {
      this._syncOfficialAccountInvites();
    }
  },
  mounted() {
    this._onDocClick = (e) => {
      if (this.actionsMenuOpen && this.$refs.actionsWrap && !this.$refs.actionsWrap.contains(e.target)) {
        this.actionsMenuOpen = false;
      }
    };
    document.addEventListener('click', this._onDocClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this._onDocClick);
  },
  async created() {
    this.isEditMode = !!this.$route.params.id;
    await Promise.all([this.loadLineConfigs(), this.loadMemberTiers(), this.loadPostbackRules()]);
    if (this.isEditMode) {
      await this.loadGroup();
    } else {
      await this.applyDefaultDisplayMode();
    }
    if (this.form.displayMode === 'member') {
      await this.loadTierUsage();
    }
  },
  methods: {
    async loadTierUsage() {
      try {
        const excludeGroupId = this.isEditMode ? this.$route.params.id : null;
        const res = await lineRichMenuService.getTierUsage(excludeGroupId);
        const body = res.data ?? res;
        this.tierUsage = body?.data ?? body ?? {};
      } catch {
        this.tierUsage = {};
      }
    },
    async applyDefaultDisplayMode() {
      try {
        const res = await clientSettingsService.get(NAMESPACES.LINE);
        const mode = res?.data?.settings?.richMenuMode;
        if (mode === 'schedule' || mode === 'member') {
          this.form.displayMode = mode;
        }
      } catch {}
    },
    async loadLineConfigs() {
      try {
        const res = await lineConfigApi.list({ pageSize: 100 });
        this.lineConfigs = res?.data?.items || res?.data || [];
        if (!this.isEditMode && this.lineConfigs.length === 1) {
          this.form.lineConfigId = this.lineConfigs[0].id;
        }
      } catch {}
    },
    async loadMemberTiers() {
      try {
        const { memberTierService } = await import('@/api/modules/memberTiers.js');
        const res = await memberTierService.getTiers({ isActive: true });
        const body = res.data ?? res;
        this.memberTiers = Array.isArray(body) ? body : (body?.items || body?.data || []);
      } catch {}
    },
    // ─── Postback Rules 載入 ───────────────────────────────
    async loadPostbackRules() {
      try {
        const res = await postbackRuleService.list({ limit: 200 });
        this.postbackRules = res.data?.data?.items || res.data?.items || [];
      } catch {}
    },

    // ─── 自動回覆規則載入（懶加載） ───────────────────────
    async ensureAutoReplyRules() {
      if (this.autoReplyRulesLoaded) return;
      try {
        let page = 1;
        const allRules = [];
        while (true) {
          const res = await autoReplyService.listRules({ pageSize: 100, page });
          const body = res.data ?? res;
          const payload = body?.data ?? body;
          const items = payload?.items || [];
          allRules.push(...items);
          const pagination = payload?.pagination;
          if (!pagination || page >= pagination.totalPages) break;
          page++;
        }
        this.autoReplyRules = allRules;
      } catch (e) {
        // listRules 載入失敗，模擬時不顯示自動回覆
      }
      this.autoReplyRulesLoaded = true;
    },

    // 渲染單一 LINE 訊息到模擬氣泡（統一 role 格式，供 LineChatMessages 使用）
    _pushLineMsg(msg) {
      // messageType 優先，避免 messageContent 內的 type 欄位覆蓋
      const type = msg.messageType || msg.type || msg.mode;
      if (type === 'text') {
        if (msg.text) {
          this.simulatedMessages.push({
            role: 'bot', messageType: 'text',
            textContent: msg.text,
            textEmojis: msg.emojis || msg.textEmojis || []
          });
        }
      } else if (type === 'image') {
        this.simulatedMessages.push({
          role: 'bot', messageType: 'image',
          imageUrl: msg.originalContentUrl || msg.imageUrl || ''
        });
      } else if (type === 'video') {
        this.simulatedMessages.push({
          role: 'bot', messageType: 'video',
          previewImageUrl: msg.previewImageUrl || ''
        });
      } else if (type === 'image_carousel') {
        // messageContent 格式：{ type: 'template', template: { type: 'image_carousel', columns: [...] } }
        const columns = msg.template?.columns || msg.carouselItems || [];
        const carouselItems = columns.map(col => ({
          imageUrl: col.imageUrl || '',
          label: col.action?.label || col.label || ''
        }));
        this.simulatedMessages.push({ role: 'bot', messageType: 'image_carousel', carouselItems });
      } else if (type === 'imagemap') {
        this.simulatedMessages.push({
          role: 'bot', messageType: 'imagemap',
          imagemapUrl: msg.baseUrl || msg.imagemapUrl || '',
          imagemapAreas: msg.actions?.map(a => ({
            x: a.area?.x / 2500, y: a.area?.y / 1686,
            w: a.area?.width / 2500, h: a.area?.height / 1686
          })) || msg.imagemapAreas || []
        });
      } else if (type === 'flex' || type === 'flex_carousel') {
        this.simulatedMessages.push({
          role: 'bot', messageType: type,
          flexContent: msg.contents || msg.flexContent || null
        });
      } else {
        this.simulatedMessages.push({ role: 'bot', messageType: type || 'unknown' });
      }
    },

    // 解析 replyContent（新格式 messages[] / 舊格式 templateCodes / templateId）並渲染
    async _simTemplateReply(replyContent) {
      if (!replyContent) return;

      // 新格式：messages 陣列
      if (Array.isArray(replyContent.messages) && replyContent.messages.length) {
        for (const msg of replyContent.messages) {
          if (msg.mode === 'text') {
            if (msg.text) this.simulatedMessages.push({ role: 'bot', messageType: 'text', textContent: msg.text, textEmojis: msg.emojis || [] });
          } else if (msg.mode === 'image') {
            this.simulatedMessages.push({ role: 'bot', messageType: 'image', imageUrl: msg.imageUrl || '' });
          } else if (msg.mode === 'template' && msg.templateId) {
            await this._simMsgTemplateById(msg.templateId);
          }
        }
        return;
      }

      // 舊格式：templateCodes[]、templateCode、templateIds[]、templateId
      const ids = Array.isArray(replyContent.templateIds) ? replyContent.templateIds
        : replyContent.templateId ? [replyContent.templateId] : [];
      const codes = Array.isArray(replyContent.templateCodes) ? replyContent.templateCodes
        : replyContent.templateCode ? [replyContent.templateCode] : [];
      for (const tid of ids) await this._simMsgTemplateById(tid);
      for (const code of codes) await this._simMsgTemplateByCode(code);
    },

    async _simMsgTemplateById(tid) {
      try {
        const res = await lineMsgTemplateService.getById(tid);
        const t = res.data?.data || res.data;
        // messageType 放在最後，避免 messageContent 內的 type 欄位蓋掉範本類型
        if (t) this._pushLineMsg({ ...(t.messageContent || {}), messageType: t.messageType });
      } catch {}
    },

    async _simMsgTemplateByCode(code) {
      try {
        const res = await lineMsgTemplateService.getByCode(code);
        const t = res.data?.data || res.data;
        if (t) this._pushLineMsg({ ...(t.messageContent || {}), messageType: t.messageType });
      } catch {}
    },

    // 關鍵字比對（對應後端 matchType: exact / contains / regex）
    _matchAutoReplyRule(text) {
      for (const rule of this.autoReplyRules) {
        const kws = Array.isArray(rule.keywords) ? rule.keywords : [];
        const mt = rule.matchType || 'contains';
        let matched = false;
        for (const kw of kws) {
          if (!kw) continue;
          if (mt === 'exact') matched = text === kw;
          else if (mt === 'contains') matched = text.includes(kw);
          else if (mt === 'regex') { try { matched = new RegExp(kw).test(text); } catch {} }
          if (matched) break;
        }
        if (matched) return rule;
      }
      return null;
    },

    // ─── 模擬互動 ─────────────────────────────────────────
    async handleSimCellClick(area) {
      if (area.actionType === 'message') {
        const text = area.actionData || area.label || '（訊息）';
        this.simulatedMessages.push({ role: 'user', text });

        // 比對自動回覆規則
        await this.ensureAutoReplyRules();
        const rule = this._matchAutoReplyRule(text);
        if (rule) {
          if (rule.replyType === 'text') {
            const replyText = (typeof rule.replyContent === 'object' ? rule.replyContent?.text : rule.replyContent) || '';
            if (replyText) this.simulatedMessages.push({ role: 'bot', messageType: 'text', textContent: replyText });
          } else if (rule.replyType === 'template') {
            await this._simTemplateReply(rule.replyContent);
          } else if (rule.replyType === 'faq') {
            const faqId = rule.replyContent?.faqId;
            if (faqId) {
              try {
                const faqRes = await apiClient.get(`/support/faq/${faqId}`);
                const faqBody = faqRes.data ?? faqRes;
                const faqData = faqBody?.data ?? faqBody;
                const answer = faqData?.answer || '';
                if (answer) this.simulatedMessages.push({ role: 'bot', messageType: 'text', textContent: answer });
              } catch {
                this.simulatedMessages.push({ role: 'tip', text: `FAQ：${rule.ruleName}` });
              }
            }
          } else {
            this.simulatedMessages.push({ role: 'tip', text: `自動回覆：${rule.ruleName}（${rule.replyType}）` });
          }
        }
        return;

      } else if (area.actionType === 'postback') {
        // 預覽官方帳號收到 postback 後回覆的推播訊息。
        // 按鈕名稱（1-1）不是聊天內容；只有另外填了「聊天室顯示的文字」才先出現使用者氣泡。
        const chatText = String(area.displayText || '').trim();
        if (chatText) {
          this.simulatedMessages.push({ role: 'user', text: chatText });
        }

        // 找對應 postback rule
        const rule = this.postbackRules.find(r => r.postbackData === area.actionData);
        if (!rule) {
          const dataLabel = area.actionData || '（未設定）';
          this.simulatedMessages.push({ role: 'tip', text: `Postback 觸發：${dataLabel}（無對應回覆規則）` });
          return;
        }

        // postback rule 用 templateIds 陣列，逐一載入並模擬回覆
        const templateIds = rule.templateIds || [];
        if (templateIds.length === 0) {
          this.simulatedMessages.push({ role: 'tip', text: `Postback 觸發：${rule.ruleName}（尚未設定回覆範本）` });
          return;
        }
        for (const tid of templateIds) {
          await this._simMsgTemplateById(tid);
        }

      } else if (area.actionType === 'uri') {
        const url = area.actionData || '（未設定網址）';
        this.simulatedMessages.push({ role: 'tip', text: `開啟：${url}` });
      } else if (area.actionType === 'share') {
        this.simulatedMessages.push({ role: 'tip', text: '開啟分享，邀請好友加入官方帳號' });
      }
    },

    // ─── LineActionField 格式轉換 ──────────────────────────────
    // area → LineActionField modelValue
    areaToAction(area) {
      const type = area.actionType || 'uri';
      const base = {
        actionType: type,
        uri: type === 'uri' ? (area.actionData || '') : '',
        text: type === 'message' ? (area.actionData || '') : '',
        data: type === 'postback' ? (area.actionData || '') : '',
        displayText: area.displayText || ''
      };
      // 分享好友／電話存的是組好的 uri，要反推回號碼與分享文字才編輯得到。
      // 剛點選、尚未輸入時 actionData 是空的，反推會得到 actionType: uri，不可蓋掉已選類型。
      if (type === 'share' || type === 'tel') {
        const inferred = actionValueFromUri(area.actionData || '');
        return {
          ...base,
          actionType: type,
          uri: inferred.uri || '',
          phone: inferred.phone || '',
          shareText: inferred.shareText || ''
        };
      }
      return base;
    },
    _syncOfficialAccountInvites() {
      const uri = buildOfficialAccountInviteUri(this.selectedLineBasicId, this.selectedLineAccountName);
      for (const tab of this.form.tabs || []) {
        for (const area of tab.areas || []) {
          if (area.actionType === 'share' && area.actionData !== uri) {
            area.actionData = uri;
          }
        }
      }
    },
    // LineActionField update → 回寫 area
    onActionUpdate(area, val) {
      const type = val.actionType || 'uri';
      area.actionType = type;
      area.displayText = val.displayText || '';
      if (type === 'uri') area.actionData = val.uri || '';
      else if (type === 'message') area.actionData = val.text || '';
      else if (type === 'postback') area.actionData = val.data || '';
      else if (type === 'share' || type === 'tel') area.actionData = val.uri || '';
      else area.actionData = '';
    },
    async loadGroup() {
      this.loading = true;
      try {
        const res = await lineRichMenuService.getById(this.$route.params.id);
        const body = res.data ?? res;
        const data = body?.data ?? body;
        this.form.name = data.name;
        this.form.lineConfigId = data.lineConfigId;
        this.form.displayMode = data.displayMode;
        this.form.startDate = data.startDate ? this._toLocalDatetime(data.startDate) : '';
        this.form.endDate = data.endDate ? this._toLocalDatetime(data.endDate) : '';
        this._syncPartsFromForm();
        this.form.tierIds = data.tierIds || [];
        this.form.tabBarSize = data.tabBarSize || 'wide';
        this.form.menuOpenByDefault = data.menuOpenByDefault === true;
        this.form.deployEnabled = data.deployEnabled === true;
        this.form.tabs = (data.tabs || []).map((t, idx) => ({
          tabIndex: idx,
          tabName: t.tabName,
          chatBarText: t.chatBarText,
          heightType: t.heightType || 'full',
          layoutType: t.layoutType,
          imageUrl: t.imageUrl || '',
          areas: this._normalizeAreas(t.areas, t.layoutType)
        }));
        if (this.form.tabs.length === 0) this.form.tabs = [DEFAULT_TAB()];
        this.groupStatus = data.status || 'draft';
        this.isBaseMenu = data.isBaseMenu === true;
        this.isLineDefaultFlag = data.isLineDefault === true;
        this.trackingEnabled = data.trackingEnabled === true;
        this.analyticsSummary = {
          trackingEnabled: data.analytics?.trackingEnabled === true,
          version: data.analytics?.version || null,
          totalClicks: Number(data.analytics?.totalClicks || 0),
          totalUniqueClicks: Number(data.analytics?.totalUniqueClicks || 0),
          buttons: Array.isArray(data.analytics?.buttons) ? data.analytics.buttons : []
        };
        this.originalTrackingStructure = this._serializeTrackingStructure(this.form.tabs);
        this.friends = data.friends || [];
      } catch (e) {
        showError('載入失敗', '');
      } finally {
        this.loading = false;
      }
    },

    // ─── Tab 管理 ──────────────────────────────────────────────
    addTab() {
      if (this.form.tabs.length >= 4) return;
      // 加第二個 Tab 時，將所有 tab 強制為全高（半高不支援多 Tab）
      if (this.form.tabs.length === 1 && this.form.tabs[0].heightType === 'half') {
        this.form.tabs[0].heightType = 'full';
        if (parseInt(this.form.tabs[0].layoutType) > 4) {
          this.selectLayout('4');
        }
      }
      const idx = this.form.tabs.length;
      this.form.tabs.push({
        ...DEFAULT_TAB(),
        tabIndex: idx,
        tabName: `Tab ${idx + 1}`
      });
      this.activeTabIndex = idx;
    },
    async removeTab(idx) {
      if (this.form.tabs.length <= 1) return;
      const tab = this.form.tabs[idx];
      const label = tab?.tabName?.trim() || `Tab ${idx + 1}`;
      const confirmed = await showConfirm(
        `確定要刪除「${label}」嗎？`,
        '刪除後該 Tab 的版型與動作設定將一併移除。',
        '刪除',
        '取消',
        'warning'
      );
      if (!confirmed) return;
      this.form.tabs.splice(idx, 1);
      this.form.tabs.forEach((t, i) => { t.tabIndex = i; });
      if (this.activeTabIndex >= this.form.tabs.length) {
        this.activeTabIndex = this.form.tabs.length - 1;
      }
    },

    // ─── Tab 列高度計算 ────────────────────────────────────────
    tabBarPx(size) {
      const basePx = size === 'wide' ? 306 : 216;
      return Math.round((basePx * this.totalHeightPx) / 1686);
    },
    buttonAreaPx(size) {
      return this.totalHeightPx - this.tabBarPx(size);
    },

    // ─── 高度切換 ──────────────────────────────────────────────
    selectHeight(heightType) {
      if (!this.currentTab) return;
      this.currentTab.heightType = heightType;
      // 半高只支援 1-4 格，若目前版型超出範圍則自動降為 4
      if (heightType === 'half' && parseInt(this.currentTab.layoutType) > 4) {
        this.selectLayout('4');
      }
    },

    // ─── 版型 ──────────────────────────────────────────────────
    selectLayout(layoutType) {
      if (!this.currentTab) return;
      const count = parseInt(layoutType, 10);
      const current = this.currentTab.areas || [];
      const newAreas = Array.from({ length: count }, (_, i) =>
        current[i] || { label: '', actionType: 'uri', actionData: '' }
      );
      this.currentTab.layoutType = layoutType;
      this.currentTab.areas = newAreas;
    },

    // ─── 圖片上傳 ──────────────────────────────────────────────
    async uploadImage(file) {
      return lineRichMenuService.uploadImage(file);
    },

    onAiImageGenerated(payload) {
      const imageUrl = typeof payload === 'string' ? payload : payload?.imageUrl;
      const areaTexts = Array.isArray(payload?.areaTexts) ? payload.areaTexts : [];
      if (this.currentTab && imageUrl) {
        this.currentTab.imageUrl = imageUrl;
        if (Array.isArray(this.currentTab.areas) && areaTexts.length > 0) {
          this.currentTab.areas = this.currentTab.areas.map((area, idx) => {
            const txt = String(areaTexts[idx] || '').trim();
            if (!txt) return area;
            return { ...area, label: txt };
          });
        }
      }
      this.aiGenerateOpen = false;
    },

    // ─── 儲存 ──────────────────────────────────────────────────
    async handleSaveDraft() {
      if (!this.form.name.trim()) { showError('請填寫選單名稱', ''); return; }
      this._ensureAutoAreaLabels();
      if (!await this._confirmTrackingResetBeforeSave('仍要儲存草稿')) return;
      this.saving = 'draft';
      try {
        const payload = { ...this._buildDraftPayload(), status: 'draft' };
        if (this.isEditMode) {
          await lineRichMenuService.update(this.$route.params.id, payload);
          showSuccess('已存為草稿', '');
          await this.loadGroup();
        } else {
          const res = await lineRichMenuService.create(payload);
          showSuccess('已存為草稿', '');
          const body = res.data ?? res;
          const newId = body?.data?.id ?? body?.id;
          if (newId) {
            this.$router.push(`/client/line/rich-menu/${newId}/edit`);
          }
        }
      } catch (e) {
        showError('儲存失敗', e.response?.data?.message || '');
      } finally {
        this.saving = false;
      }
    },

    // ─── 儲存建立選單 ──────────────────────────────────────────────
    async handleActivate() {
      if (!await this._validate()) return;
      if (!await this._confirmTrackingResetBeforeSave('仍要建立選單')) return;
      const confirmed = await showConfirm(
        '儲存建立選單',
        '系統將在 LINE 建立圖文選單結構，但不會自動套用到好友。建立後可按「立即套用」指派給使用者。',
        '確定建立',
        '取消',
        'question'
      );
      if (!confirmed) return;
      this.activating = true;
      try {
        const payload = this._buildPayload();
        if (this.isEditMode) {
          await lineRichMenuService.update(this.$route.params.id, payload);
          await lineRichMenuService.activate(this.$route.params.id);
          showSuccess('啟用成功', '圖文選單已在 LINE 建立，可按「立即套用」設為預設。');
          await this.loadGroup();
        } else {
          const res = await lineRichMenuService.create(payload);
          const body = res.data ?? res;
          const newId = body?.data?.id ?? body?.id;
          if (newId) {
            await lineRichMenuService.activate(newId);
            showSuccess('啟用成功', '圖文選單已在 LINE 建立，可在編輯頁按「立即套用」設為預設。');
            this.$router.push(`/client/line/rich-menu/${newId}/edit`);
          }
        }
      } catch (e) {
        console.error('[activate] 完整錯誤:', e.response?.data ?? e);
        showError('啟用失敗', e.response?.data?.message || '');
      } finally {
        this.activating = false;
      }
    },

    // ─── 立即套用 ──────────────────────────────────────────────
    async handleApplyAsDefault() {
      if (!await this._validate()) return;

      // 排程模式：若結束時間已過，套用後排程系統將立即下架，無法取代基本選單
      if (this.form.displayMode === 'schedule' && this.form.endDate && new Date(this.form.endDate) < new Date()) {
        const formattedDate = new Date(this.form.endDate).toLocaleString('zh-TW', {
          year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
        });
        await Swal.fire({
          icon: 'warning',
          title: '無法套用',
          html: `此圖文選單的有效期間已結束（${formattedDate}），<br>套用後排程系統將立即再次將其下架，<strong>無法取代基本選單</strong>。<br><br>請先更新或移除結束日期後再套用。`,
          confirmButtonText: '我知道了',
          confirmButtonColor: '#6c757d',
        });
        return;
      }

      const isSchedule = this.form.displayMode === 'schedule';
      let confirmed;
      if (isSchedule) {
        const result = await Swal.fire({
          icon: 'question',
          title: '儲存並立即套用',
          html: '系統將先儲存設定並重新建立 LINE 選單結構，再設為 LINE 頻道預設選單。<br><br>' +
            '<ul style="text-align:left;padding-left:1.2em;margin:0;">' +
            '<li>若有其他有效的排程選單，將自動改回草稿。</li>' +
            '</ul>',
          showCancelButton: true,
          confirmButtonText: '確定套用',
          cancelButtonText: '取消',
          confirmButtonColor: '#dc3545',
          cancelButtonColor: '#6c757d',
        });
        confirmed = result.isConfirmed;
      } else {
        confirmed = await showConfirm(
          '儲存並立即套用',
          '系統將先儲存設定並重新建立 LINE 選單結構，再連結到符合等級的好友。',
          '確定套用',
          '取消',
          'question'
        );
      }
      if (!confirmed) return;
      if (!await this._confirmTrackingResetBeforeSave('仍要儲存並套用')) return;
      this.applying = true;
      try {
        // 先儲存最新設定並重建 LINE 結構（activateOnly），再套用
        const payload = this._buildPayload();
        await lineRichMenuService.update(this.$route.params.id, payload);
        await lineRichMenuService.activate(this.$route.params.id);
        const applyRes = await lineRichMenuService.applyAsDefault(this.$route.params.id);
        const applyData = applyRes?.data?.data;
        if (this.form.displayMode === 'member' && applyData?.asyncLinking) {
          showSuccess('套用中', '會員人數較多，連結作業正在背景進行，稍後將自動完成。', 5000);
        } else {
          showSuccess('已儲存並立即套用', this.form.displayMode === 'schedule' ? '此選單已設為 LINE 頻道預設選單。' : '已連結到符合等級的好友。');
        }
        await this.loadGroup();
      } catch (e) {
        console.error('[apply-as-default] 完整錯誤:', e.response?.data ?? e);
        showError('套用失敗', e.response?.data?.message || '');
      } finally {
        this.applying = false;
      }
    },

    async handleToggleBaseMenu() {
      if (this.isBaseMenu) {
        const confirmed = await showConfirm(
          '取消基本選單',
          '取消後，排程選單到期時將不再自動 fallback 到此選單。',
          '確定取消', '返回', 'warning'
        );
        if (!confirmed) return;
        this.settingBase = true;
        try {
          await lineRichMenuService.unsetBaseMenu(this.$route.params.id);
          showSuccess('已取消基本選單', '');
          await this.loadGroup();
        } catch (e) {
          showError('操作失敗', e.response?.data?.message || '');
        } finally {
          this.settingBase = false;
        }
      } else {
        const confirmed = await showConfirm(
          '設為基本選單',
          '此選單將立即取代 LINE 目前的預設選單，並成為排程到期後的基本選單。同一帳號只能有一個基本選單。',
          '確定取代', '取消', 'question'
        );
        if (!confirmed) return;
        this.settingBase = true;
        try {
          await lineRichMenuService.setBaseMenu(this.$route.params.id);
          showSuccess('基本選單已變更', '此選單已立即取代原本的 LINE 預設選單。');
          await this.loadGroup();
        } catch (e) {
          showError('設定失敗', e.response?.data?.message || '');
        } finally {
          this.settingBase = false;
        }
      }
    },

    handleCancel() {
      this.$router.push('/client/line/rich-menu');
    },

    // ─── 測試好友 ──────────────────────────────────────────────
    searchLineFriends() {
      this.selectedLineFriend = null;
      if (this.lineFriendSearchTimer) clearTimeout(this.lineFriendSearchTimer);
      if (!this.lineFriendQuery || this.lineFriendQuery.length < 1) {
        this.lineFriendResults = [];
        return;
      }
      this.lineFriendSearchTimer = setTimeout(async () => {
        try {
          const res = await lineRichMenuService.searchLineFriends(
            this.$route.params.id, this.lineFriendQuery
          );
          const body = res?.data ?? res;
          this.lineFriendResults = Array.isArray(body) ? body : (body?.data ?? []);
        } catch {
          this.lineFriendResults = [];
        }
      }, 300);
    },
    selectLineFriend(f) {
      this.selectedLineFriend = f;
      this.lineFriendQuery = f.displayName;
      this.lineFriendResults = [];
      this.addFriendDirect();
    },
    async addFriendDirect() {
      if (!this.isEditMode || !this.selectedLineFriend) return;
      try {
        const res = await lineRichMenuService.addFriend(this.$route.params.id, {
          lineUserId: this.selectedLineFriend.lineUserId,
          memberName: this.selectedLineFriend.displayName,
        });
        const resData = res?.data?.data ?? res?.data ?? res ?? {};
        this.lineFriendQuery = '';
        this.lineFriendResults = [];
        this.selectedLineFriend = null;
        await this.loadGroup();
        if (resData.linked) {
          showSuccess('已加入並立即更新選單', '好友已成功連結到此圖文選單');
        } else if (this.groupStatus === 'active') {
          showSuccess('已加入', '好友已記錄，但連結未能即時完成，請重新啟用或立即套用');
        } else {
          showSuccess('已加入', '啟用後將自動為此好友指派圖文選單');
        }
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || '';
        showError('加入失敗', msg);
      }
    },
    async removeFriend(friend) {
      if (!confirm(`確定要移除「${friend.memberName || friend.lineUserId}」的測試指派嗎？`)) return;
      try {
        await lineRichMenuService.removeFriend(
          this.$route.params.id, friend.id, friend.lineUserId
        );
        await this.loadGroup();
        if (this.groupStatus === 'active') {
          showSuccess('已移除並解除選單指派', '該好友的圖文選單已從 LINE 側取消連結');
        } else {
          showSuccess('已移除', '');
        }
      } catch (e) {
        showError('移除失敗', '');
      }
    },

    // ─── 工具 ──────────────────────────────────────────────────

    handleAnalyticsSummary(summary) {
      this.analyticsSummary = {
        trackingEnabled: summary?.trackingEnabled === true,
        version: summary?.version || null,
        totalClicks: Number(summary?.totalClicks || 0),
        totalUniqueClicks: Number(summary?.totalUniqueClicks || 0),
        buttons: Array.isArray(summary?.buttons) ? summary.buttons : []
      };
      this.trackingEnabled = this.trackingEnabled || this.analyticsSummary.trackingEnabled;
    },

    _serializeTrackingStructure(tabs) {
      return JSON.stringify((tabs || []).map((tab, tabIndex) => ({
        tabIndex,
        layoutType: String(tab.layoutType || ''),
        heightType: tab.heightType === 'half' ? 'half' : 'full',
        areas: (tab.areas || []).map(area => ({
          label: area.label || '',
          actionType: area.actionType || '',
          actionData: area.actionData || ''
        }))
      })));
    },

    async _confirmTrackingResetBeforeSave(confirmLabel = '仍要儲存') {
      if (!this.isEditMode || !this.trackingEnabled) return true;
      const nextStructure = this._serializeTrackingStructure(this.form.tabs);
      if (nextStructure === this.originalTrackingStructure) return true;

      try {
        const response = await lineRichMenuService.previewAnalyticsReset(
          this.$route.params.id,
          this.form.tabs
        );
        const body = response?.data ?? response ?? {};
        const preview = body?.data ?? body;
        if (!preview.willResetOnNextDeploy) return true;

        return showConfirm(
          '此變更將在下次套用後重置點擊統計',
          `目前為第 ${preview.currentVersionNo || '—'} 版統計。儲存本身不會立即清除資料，但下次成功套用後會建立新統計版本並從 0 開始累計。`,
          confirmLabel,
          '取消',
          'warning'
        );
      } catch (error) {
        showError(
          '無法確認統計重置影響',
          error.response?.data?.message || error.message || '請稍後再試'
        );
        return false;
      }
    },

    // 草稿用：只送必要欄位，不需完整驗證
    _buildDraftPayload() {
      const payload = {
        name: this.form.name,
        lineConfigId: this.form.lineConfigId || null,
        startDate: this._truncateToHour(this.form.startDate),
        endDate: this._truncateToHour(this.form.endDate),
        tierIds: this.form.tierIds,
        tabBarSize: this.form.tabBarSize,
        menuOpenByDefault: this.form.menuOpenByDefault === true,
        tabs: this.form.tabs.map((tab, idx) => ({
          tabIndex: idx,
          tabName: tab.tabName,
          chatBarText: tab.chatBarText,
          heightType: tab.heightType || 'full',
          layoutType: tab.layoutType,
          imageUrl: tab.imageUrl,
          areas: tab.areas
        }))
      };
      if (!this.isEditMode) {
        payload.displayMode = this.form.displayMode;
      }
      return payload;
    },

    _buildPayload() {
      const payload = {
        name: this.form.name,
        lineConfigId: this.form.lineConfigId,
        startDate: this._truncateToHour(this.form.startDate),
        endDate: this._truncateToHour(this.form.endDate),
        deployEnabled: this.form.deployEnabled === true,
        tierIds: this.form.tierIds,
        tabBarSize: this.form.tabBarSize,
        menuOpenByDefault: this.form.menuOpenByDefault === true,
        tabs: this.form.tabs.map((tab, idx) => ({
          tabIndex: idx,
          tabName: tab.tabName,
          chatBarText: tab.chatBarText,
          heightType: tab.heightType || 'full',
          layoutType: tab.layoutType,
          imageUrl: tab.imageUrl,
          areas: tab.areas
        }))
      };
      // displayMode 只在新增時設定，編輯時不傳（後端不允許變更）
      if (!this.isEditMode) {
        payload.displayMode = this.form.displayMode;
      }
      return payload;
    },

    /** 留白按鈕名稱時，存檔前自動填為「Tab序號-按鈕序號」（1-based，如 1-1、1-2） */
    _ensureAutoAreaLabels() {
      this.form.tabs.forEach((tab, ti) => {
        const areaCount = parseInt(tab.layoutType || '6', 10);
        if (!Array.isArray(tab.areas)) return;
        for (let ai = 0; ai < areaCount; ai++) {
          const area = tab.areas[ai];
          if (!area) continue;
          if (!String(area.label ?? '').trim()) {
            area.label = `${ti + 1}-${ai + 1}`;
          }
        }
      });
    },

    async _validate() {
      if (!this.form.name.trim()) { showError('請填寫選單名稱', ''); return false; }
      if (!this.form.lineConfigId) { showError('請選擇 LINE 帳號', ''); return false; }

      if (this.form.displayMode === 'schedule') {
        const hasStart = !!this.form.startDate;
        const hasEnd = !!this.form.endDate;

        // 開始時間與結束時間需同時設定或同時留空
        if (hasStart !== hasEnd) {
          showError('日期設定不完整', '開始時間與結束時間需同時設定，或同時留空（不設定則不會自動上下架）');
          return false;
        }

        if (hasStart && hasEnd) {
          if (new Date(this.form.startDate) >= new Date(this.form.endDate)) {
            showError('結束時間必須晚於開始時間', ''); return false;
          }
          // 檢查與其他選單的時間重疊
          const overlap = await this._checkDateOverlap();
          if (overlap) return false;
        }
      }

      this._ensureAutoAreaLabels();

      // 每個 Tab 必須完整填寫必要欄位才能建立（否則請存為草稿）
      for (let ti = 0; ti < this.form.tabs.length; ti++) {
        const tab = this.form.tabs[ti];
        const tabLabel = tab.tabName?.trim() ? `Tab「${tab.tabName}」` : `第 ${ti + 1} 個 Tab`;

        if (!tab.tabName?.trim()) {
          showError('Tab 名稱未填寫', `${tabLabel} 請填寫 Tab 名稱`);
          this.activeTabIndex = ti;
          return false;
        }
        if (!tab.chatBarText?.trim()) {
          showError('選單欄位文字未填寫', `${tabLabel} 請填寫選單欄位文字（顯示在聊天室底部的文字）`);
          this.activeTabIndex = ti;
          return false;
        }
        if (!tab.imageUrl) {
          showError('背景圖片未上傳', `${tabLabel} 請上傳背景圖片`);
          this.activeTabIndex = ti;
          return false;
        }

        // 依版型取得實際按鈕數量，逐一檢查
        const areaCount = parseInt(tab.layoutType || '6', 10);
        const areas = (tab.areas || []).slice(0, areaCount);
        for (let ai = 0; ai < areas.length; ai++) {
          const area = areas[ai];
          const areaLabel = `${tabLabel} 第 ${ai + 1} 個按鈕`;

          if (area.actionType === 'share' && !area.actionData?.trim()) {
            showError('無法邀請加入官方帳號', `${areaLabel} 請先確認 LINE 帳號已填寫 Basic ID（@開頭）`);
            this.activeTabIndex = ti;
            return false;
          }
          if (area.actionType !== 'switchTab' && !area.actionData?.trim()) {
            showError('按鈕連結未填寫', `${areaLabel} 請填寫按鈕連結或動作內容`);
            this.activeTabIndex = ti;
            return false;
          }
          if (area.actionType === 'uri' && area.actionData) {
            try {
              const u = new URL(area.actionData);
              if (!['http:', 'https:', 'line:'].includes(u.protocol)) throw new Error();
            } catch {
              showError('網址格式不正確', `${areaLabel} 網址格式錯誤，請確認包含 https://`);
              this.activeTabIndex = ti;
              return false;
            }
          }
          if (area.actionType === 'switchTab' && (area.actionData === '' || area.actionData === null || area.actionData === undefined)) {
            showError('切換 Tab 未設定', `${areaLabel} 選擇了「切換 Tab」但尚未指定要切換到哪個 Tab`);
            this.activeTabIndex = ti;
            return false;
          }
        }
      }

      return true;
    },

    async _checkDateOverlap() {
      try {
        const res = await lineRichMenuService.list({
          lineConfigId: this.form.lineConfigId,
          displayMode: 'schedule',
          pageSize: 200,
        });
        const body = res.data;
        const payload = body?.data ?? body;
        const items = payload?.items ?? [];
        const currentId = this.isEditMode ? this.$route.params.id : null;
        const start = new Date(this.form.startDate);
        const end = new Date(this.form.endDate);

        for (const item of items) {
          if (item.id === currentId) continue;
          if (!item.startDate || !item.endDate) continue;
          if (item.status === 'expired') continue;

          const iStart = new Date(item.startDate);
          const iEnd = new Date(item.endDate);

          if (start < iEnd && end > iStart) {
            const fmt = d => new Date(d).toLocaleString('zh-TW', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
            showError(
              '顯示期間重疊',
              `與「${item.name}」的期間重疊（${fmt(item.startDate)} ～ ${fmt(item.endDate)}），請調整時間。`
            );
            return true;
          }
        }
        return false;
      } catch {
        return false;
      }
    },

    _normalizeAreas(areas, layoutType) {
      const count = parseInt(layoutType || '6', 10);
      const base = Array.isArray(areas) ? areas : [];
      return Array.from({ length: count }, (_, i) =>
        base[i] || { label: '', actionType: 'uri', actionData: '' }
      );
    },
    _toLocalDatetime(iso) {
      const d = new Date(iso);
      const pad = n => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:00`;
    },
    _syncPartsFromForm() {
      const parse = (dt) => {
        if (!dt) return { date: '', hour: '00' };
        const [datePart, timePart] = dt.split('T');
        return { date: datePart || '', hour: (timePart || '00').substring(0, 2) };
      };
      const s = parse(this.form.startDate);
      const e = parse(this.form.endDate);
      this.scheduleParts.startDate = s.date;
      this.scheduleParts.startHour = s.hour;
      this.scheduleParts.endDate   = e.date;
      this.scheduleParts.endHour   = e.hour;
    },
    _syncFormFromParts() {
      this.form.startDate = this.scheduleParts.startDate
        ? `${this.scheduleParts.startDate}T${this.scheduleParts.startHour}:00`
        : '';
      this.form.endDate = this.scheduleParts.endDate
        ? `${this.scheduleParts.endDate}T${this.scheduleParts.endHour}:00`
        : '';
    },
    _truncateToHour(datetimeLocal) {
      if (!datetimeLocal) return null;
      // datetimeLocal 是 'YYYY-MM-DDTHH:00'（瀏覽器本地時間，無時區）
      // new Date() 會當作本地時間解析，再轉 UTC ISO 送後端，避免時區偏移 8 小時
      const d = new Date(datetimeLocal);
      d.setMinutes(0, 0, 0);
      return d.toISOString();
    }
  }
};
</script>

<style scoped>
/* 浮動預覽面板 */
.preview-sticky {
  position: fixed;
  top: 120px;
  right: 24px;
  width: 340px;
  max-height: calc(100vh - 140px);
  overflow-x: hidden;
  overflow-y: auto;
  z-index: 90;
  background: transparent;
  padding: 0;
}
.preview-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  padding: 0 4px 8px;
  letter-spacing: 0.3px;
}
.preview-title i { font-size: 14px; color: #06C755; }

/* 頁首 */
.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-right: 0;
}

.page-container {
  padding-top: 72px;
}

.page-title { font-size: 20px; font-weight: 600; margin: 0; }
.header-left { display: flex; flex-direction: row; align-items: center; gap: 10px; flex-wrap: wrap; }
.header-actions { display: flex; gap: 8px; align-items: center; }
.header-with-preview { margin-right: 0; }

/* 動作下拉選單 */
.actions-dropdown-wrap {
  position: relative;
}
.btn-actions {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 14px;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  white-space: nowrap;
}
.btn-actions:hover { background: #f3f4f6; border-color: #9ca3af; }
.btn-actions i { font-size: 16px; }
.actions-dropdown-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 200px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,.12);
  z-index: 300;
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.actions-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: #374151;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s, color 0.12s;
  white-space: nowrap;
}
.actions-menu-item:hover:not(:disabled) { background: #f3f4f6; }
.actions-menu-item:disabled { opacity: 0.5; cursor: not-allowed; }
.actions-menu-item i { font-size: 15px; flex-shrink: 0; }
.actions-menu-item.item-activate { color: #059669; }
.actions-menu-item.item-activate:hover:not(:disabled) { background: #f0fdf4; }
.actions-menu-item.item-apply { color: #7c3aed; }
.actions-menu-item.item-apply:hover:not(:disabled) { background: #f5f3ff; }
.actions-menu-item.item-base { color: #d97706; }
.actions-menu-item.item-base:hover:not(:disabled) { background: #fffbeb; }
.actions-menu-item.item-base-active { color: #b45309; font-weight: 600; }
.actions-menu-item.item-base-active:hover:not(:disabled) { background: #fef3c7; }

.status-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
}
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.status-badge.draft   { background: #f3f4f6; color: #6b7280; }
.status-badge.active  { background: #dcfce7; color: #166534; }
.status-badge.active.line-default { background: #7c3aed; color: #fff; }
.status-badge.expired { background: #fee2e2; color: #991b1b; }
.base-menu-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 12px;
  background: #fef3c7;
  color: #b45309;
  font-size: 12px;
  font-weight: 600;
}

.tracking-change-warning {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 380px 16px 0;
  padding: 12px 16px;
  border: 1px solid #fcd34d;
  border-radius: 10px;
  background: #fffbeb;
  color: #78350f;
  font-size: 13px;
  line-height: 1.55;
}
.tracking-change-warning > i { margin-top: 2px; color: #d97706; font-size: 18px; flex-shrink: 0; }
.tracking-change-warning strong { font-weight: 700; }
.tracking-change-warning p { margin: 2px 0 0; }

/* 主編輯區（桌機向左縮） */
/* ─── 頁籤導航 ──────────────────────────────────── */
.editor-tab-nav {
  display: flex;
  gap: 0;
  margin-bottom: 20px;
  margin-right: 380px;
  background: #fff;
  border-radius: 12px 12px 0 0;
  border-bottom: 2px solid #e5e7eb;
  padding: 0 4px;
}
.editor-tab-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 20px;
  border: none;
  border-bottom: 3px solid transparent;
  border-radius: 0;
  background: transparent;
  color: #9ca3af;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
  white-space: nowrap;
  margin-bottom: -2px;
  position: relative;
}
.editor-tab-btn:hover {
  color: #374151;
  background: #f9fafb;
  border-radius: 8px 8px 0 0;
}
.editor-tab-btn.active {
  color: #06C755;
  font-weight: 700;
  border-bottom-color: #06C755;
  background: transparent;
}
.editor-tab-btn.active:hover {
  background: transparent;
}
.editor-tab-btn i { font-size: 16px; }

.editor-layout { margin-right: 380px; }

/* 手機版開關 */
.mobile-preview-toggle-bar { display: none; }
.mobile-preview-drawer { display: none; }

/* ─── Editor 區塊 ─────────────────────────────── */
.editor-section {
  background: #fff;
  border-radius: 12px;
  padding: 24px 28px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,.06);
}
.section-header-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}
.section-header-row h3 { margin: 0; font-size: 16px; font-weight: 600; }
.section-icon { font-size: 18px; color: #06C755; }
.section-hint { margin-left: auto; font-size: 12px; color: #9ca3af; }

.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.form-group { display: flex; flex-direction: column; gap: 6px; }
.form-label { font-size: 13px; font-weight: 500; color: #374151; }
.required-label::after { content: ' *'; color: #ef4444; }
.form-control, .form-select {
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #374151;
  background: #fff;
}
.form-control:focus, .form-select:focus {
  outline: none;
  border-color: #06C755;
  box-shadow: 0 0 0 2px rgba(6,199,85,.15);
}
.form-hint { font-size: 12px; color: #9ca3af; margin: 2px 0 8px; }

/* Tab 切換列 */
/* ── Tab 頁籤卡片 ─────────────────────────────────── */
.tab-panel {
  border: 1.5px solid #d1d5db;
  border-radius: 12px;
  background: #fff;
  margin-top: 16px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,.06);
}

/* 頁籤列 */
.tab-header {
  display: flex;
  align-items: stretch;
  background: #f3f4f6;
  border-bottom: 1.5px solid #d1d5db;
  padding: 0 12px;
  gap: 0;
  min-height: 44px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}
.tab-header::-webkit-scrollbar { display: none; }
.tab-header-tabs {
  display: flex;
  gap: 4px;
  flex-wrap: nowrap;
  align-items: flex-end;
  flex-shrink: 0;
}
.tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  height: 44px;
  border: none;
  border-bottom: 3px solid transparent;
  border-radius: 0;
  background: transparent;
  color: #6b7280;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: color .15s, border-color .15s, background .15s;
  position: relative;
  white-space: nowrap;
}
.tab-btn:hover:not(.active) {
  color: #374151;
  background: rgba(0,0,0,.04);
}
.tab-btn.active {
  border-bottom-color: #06C755;
  color: #059669;
  font-weight: 700;
  background: transparent;
}
.tab-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(0,0,0,.1);
  color: #6b7280;
  font-size: 11px;
  cursor: pointer;
  flex-shrink: 0;
  transition: background .15s, color .15s;
}
.tab-remove:hover { background: #ef4444; color: #fff; }

/* 新增 Tab 按鈕 */
.btn-add-tab {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0 16px;
  height: 32px;
  align-self: center;
  margin-left: 8px;
  border: 1.5px solid #06C755;
  border-radius: 20px;
  background: #06C755;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background .15s, border-color .15s, transform .1s;
  white-space: nowrap;
  flex-shrink: 0;
}
.btn-add-tab:hover { background: #05b34c; border-color: #05b34c; transform: translateY(-1px); }
.btn-add-tab:active { transform: translateY(0); }
.btn-add-tab i { font-size: 14px; }

/* Tab 內容區 */
.tab-editor {
  padding: 20px;
}

/* 基本設定：預設顯示 / 收起 */
.menu-open-default-group {
  margin-top: 20px;
  padding: 14px 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}
.menu-open-hint {
  margin: 6px 0 10px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.55;
}
.menu-open-toggle {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
.menu-open-opt {
  flex: 1;
  min-width: 140px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 12px 14px;
  background: #fff;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, background 0.15s;
}
.menu-open-opt > i {
  font-size: 22px;
  color: #9ca3af;
  margin-bottom: 2px;
}
.menu-open-opt.active {
  border-color: #25D366;
  background: #f0fff5;
}
.menu-open-opt.active > i {
  color: #059669;
}
.menu-open-opt .opt-title {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.menu-open-opt .opt-desc {
  font-size: 11px;
  color: #6b7280;
  line-height: 1.45;
}

/* Tab 列高度選擇 */
.tab-bar-size-group {
  margin-top: 12px;
  padding: 14px 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}
.tab-bar-size-toggle {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}
.tab-size-btn {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: #fff;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.tab-size-btn.active {
  border-color: #25D366;
  background: #f0fff5;
}
.tab-size-visual {
  display: flex;
  flex-direction: column;
  width: 36px;
  height: 36px;
  border: 1.5px solid #d1d5db;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
}
.tab-size-bar {
  background: #25D366;
  width: 100%;
}
.tab-size-body {
  flex: 1;
  background: #e5e7eb;
}
.tab-size-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
}
.tab-size-info strong {
  font-size: 13px;
  color: #111827;
}
.tab-size-info span {
  font-size: 11px;
  color: #6b7280;
}
.ai-generate-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}
.btn-ai-generate {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid #7c3aed;
  background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%);
  color: #5b21b6;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.btn-ai-generate:hover {
  background: #ede9fe;
}
.ai-generate-hint {
  font-size: 12px;
  color: #888;
}
.image-cut-guide {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 10px;
  padding: 8px 12px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 6px;
  font-size: 12px;
  color: #78350f;
  line-height: 1.6;
}
.image-cut-guide i {
  font-size: 14px;
  margin-top: 1px;
  flex-shrink: 0;
}
.image-cut-guide strong {
  color: #b45309;
}

/* 高度選擇 */
.height-toggle {
  display: flex;
  gap: 12px;
}
.height-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  flex: 1;
  text-align: left;
  transition: all .15s;
  position: relative;
}
.height-btn.active { border-color: #06C755; background: #f0fff5; }
.height-btn.disabled { opacity: 0.5; cursor: not-allowed; }
.height-btn:not(.disabled):hover { border-color: #06C755; }

.height-preview {
  width: 36px;
  flex-shrink: 0;
  border: 1px solid #d1d5db;
  border-radius: 3px;
  background: #f3f4f6;
  display: flex;
  align-items: flex-end;
  overflow: hidden;
}
.height-full { height: 36px; }
.height-half { height: 18px; }
.height-fill { width: 100%; background: #d1fae5; flex: 1; }

.height-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.height-info-title { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.height-info strong { font-size: 13px; color: #111827; }
.height-info span { font-size: 11px; color: #9ca3af; }
.height-layouts { color: #6b7280 !important; }
.height-single-only { font-size: 11px; font-weight: 700; color: #dc2626 !important; white-space: nowrap; }
.height-btn.active .height-info strong { color: #059669; }
.height-lock {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 11px;
  color: #9ca3af;
  display: flex;
  align-items: center;
  gap: 3px;
}

/* 版型選擇 */
.layout-picker {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.layout-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 8px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  min-width: 64px;
  transition: all .15s;
}
.layout-btn.active { border-color: #06C755; background: #f0fff5; }
.layout-btn:hover { border-color: #06C755; }
.layout-label { font-size: 11px; color: #6b7280; }
.layout-btn.active .layout-label { color: #059669; font-weight: 600; }

.layout-preview {
  width: 48px;
  aspect-ratio: 2500 / 1686;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  display: grid;
  gap: 1px;
  overflow: hidden;
  background: #e5e7eb;
}
.layout-preview.half-mode { aspect-ratio: 2500 / 843; }
.layout-preview .layout-cell { background: #fff; }
.layout-preview.layout-1 { grid-template-columns: 1fr; }
.layout-preview.layout-2 { grid-template-columns: 1fr 1fr; }
.layout-preview.layout-3 { grid-template-columns: 1fr 1fr 1fr; }
.layout-preview.layout-4 { grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr; }
.layout-preview.layout-6 { grid-template-columns: 1fr 1fr 1fr; grid-template-rows: 1fr 1fr; }
.layout-preview.layout-8 { grid-template-columns: 1fr 1fr 1fr 1fr; grid-template-rows: 1fr 1fr; }
/* 半高模式：4格改為橫排4欄 */
.layout-preview.half-mode.layout-4 { grid-template-columns: 1fr 1fr 1fr 1fr; grid-template-rows: 1fr; }

/* 按鈕設定 */
.areas-list { display: flex; flex-direction: column; gap: 10px; }
.area-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
}
.area-header { margin-bottom: 8px; }
.area-num {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  background: #f3f4f6;
  padding: 2px 8px;
  border-radius: 4px;
}
.area-fields {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-select.select-error { border-color: #ef4444; }
.form-select.select-error:focus { outline: none; border-color: #ef4444; box-shadow: 0 0 0 2px rgba(239,68,68,.12); }
.field-error-inline {
  display: flex; align-items: center; gap: 4px;
  font-size: 11px; color: #ef4444; margin-top: 2px;
}
.field-error-inline i { font-size: 12px; }

/* 顯示模式 */
.mode-change-hint {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #9ca3af;
  margin: 8px 0 0;
}
.mode-change-hint i { font-size: 13px; flex-shrink: 0; }
.mode-change-hint a { color: #667eea; text-decoration: none; }
.mode-change-hint a:hover { text-decoration: underline; }

.mode-settings { padding-top: 4px; }
.hour-picker {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.hour-picker .form-control { flex: 1; min-width: 110px; }
.hour-select { flex: 0 0 90px !important; }

/* 會員等級複選 */
.tier-checkboxes { display: flex; flex-wrap: wrap; gap: 8px; }
.tier-checkbox {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1.5px solid #e5e7eb;
  border-radius: 20px;
  cursor: pointer;
  font-size: 13px;
  transition: all .15s;
}
.tier-checkbox input { display: none; }
.tier-checkbox.checked { border-color: #06C755; background: #f0fff5; color: #059669; font-weight: 500; }
.tier-checkbox.disabled {
  border-color: #e5e7eb;
  background: #f9fafb;
  color: #9ca3af;
  cursor: not-allowed;
  opacity: 0.7;
}
.tier-conflict-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  color: #9ca3af;
  margin-left: 2px;
}
.tier-conflict-tag i { font-size: 12px; }

/* 顯示模式：建立後鎖定提示 */
.mode-locked-badge {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #9ca3af;
  background: #f3f4f6;
  padding: 3px 10px;
  border-radius: 12px;
}
.mode-locked-badge i { font-size: 13px; }

/* 顯示模式：唯讀狀態 */
.mode-readonly { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.mode-readonly-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
}
.mode-readonly-badge i { font-size: 20px; }
.mode-readonly-badge.schedule { background: #eff6ff; color: #3b82f6; }
.mode-readonly-badge.member   { background: #fef9c3; color: #92400e; }
.mode-readonly-desc { font-size: 13px; color: #6b7280; margin: 0; flex: 1; }

/* 提示框 */
.hint-box {
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 8px;
}
.hint-info { background: #eff6ff; color: #1d4ed8; }
.hint-warning { background: #fef3c7; color: #92400e; }
.hint-success { background: #f0fdf4; color: #166534; border-left: 3px solid #22c55e; }

/* 排程時間說明 */
.schedule-time-guide {
  margin-top: 12px;
  margin-bottom: 4px;
  border: 1px solid #e0e7ff;
  border-radius: 8px;
  background: #f5f7ff;
  font-size: 13px;
  color: #374151;
  overflow: hidden;
}
.schedule-time-guide__title {
  background: #e0e7ff;
  color: #3730a3;
  font-weight: 600;
  padding: 7px 14px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.schedule-time-guide__body {
  padding: 10px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.schedule-time-guide__row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  line-height: 1.6;
}
.schedule-time-guide__label {
  flex: 0 0 60px;
  font-weight: 600;
  color: #4338ca;
  padding-top: 1px;
}
.schedule-time-guide__example {
  display: block;
  margin-top: 3px;
  color: #6b7280;
}
.schedule-time-guide__row--tip {
  color: #92400e;
  background: #fef9c3;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12.5px;
  gap: 6px;
  align-items: center;
}

/* 測試好友 */
.friend-search-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-bottom: 12px;
}
.friend-search-input {
  flex: 1;
  min-width: 0;
}
.line-friend-search { position: relative; }
.line-friend-dropdown {
  position: absolute;
  z-index: 200;
  left: 0; right: 0;
  top: calc(100% + 4px);
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,.1);
  max-height: 240px;
  overflow-y: auto;
}
.line-friend-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 13px;
}
.line-friend-option:hover { background: #f3f4f6; }
.lf-avatar { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
.lf-avatar-icon { font-size: 24px; color: #9ca3af; flex-shrink: 0; }
.lf-name { font-weight: 500; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.lf-uid { font-size: 11px; color: #9ca3af; flex-shrink: 0; }
.btn-add-friend {
  flex-shrink: 0;
  white-space: nowrap;
  align-self: center;
}
.friends-list { display: flex; flex-direction: column; gap: 8px; }
.friend-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #f9fafb;
  border-radius: 8px;
}
.friend-item > i { font-size: 18px; color: #9ca3af; }
.friend-info { flex: 1; min-width: 0; }
.friend-name { display: block; font-size: 14px; font-weight: 500; }
.friend-line-id { font-size: 11px; color: #9ca3af; }
.friend-linked-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 500;
  color: #059669;
  background: #d1fae5;
  border-radius: 4px;
  padding: 2px 7px;
  white-space: nowrap;
}
.friend-pending-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 500;
  color: #92400e;
  background: #fef3c7;
  border-radius: 4px;
  padding: 2px 7px;
  white-space: nowrap;
}
.empty-friends { font-size: 13px; color: #9ca3af; text-align: center; padding: 20px; }

.btn-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: #f3f4f6;
  color: #374151;
  cursor: pointer;
  font-size: 14px;
}
.btn-icon:hover { background: #e5e7eb; }
.delete-btn { color: #ef4444; }
.delete-btn:hover { background: #fef2f2; }

.btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border: none; border-radius: 8px; font-size: 14px; font-weight: 500; cursor: pointer; }
.btn-primary { background: #06C755; color: #fff; }
.btn-primary:hover { background: #05a847; }
.btn-secondary { background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; }
.btn-secondary:hover { background: #e5e7eb; }
.btn-success { background: #0ea5e9; color: #fff; }
.btn-success:hover { background: #0284c7; }
.btn-draft { background: #fff; color: #6b7280; border: 1.5px solid #d1d5db; }
.btn-draft:hover:not(:disabled) { border-color: #9ca3af; color: #374151; background: #f9fafb; }
.btn-activate { background: #06C755; color: #fff; }
.btn-activate:hover:not(:disabled) { background: #05a847; }
.btn-apply { background: #7c3aed; color: #fff; }
.btn-apply:hover:not(:disabled) { background: #6d28d9; }
.btn-base { background: #fff; color: #d97706; border: 1.5px solid #fbbf24; }
.btn-base:hover:not(:disabled) { background: #fffbeb; border-color: #f59e0b; }
.btn-base-active { background: #f59e0b; color: #fff; }
.btn-base-active:hover:not(:disabled) { background: #d97706; }
.btn:disabled { opacity: 0.6; cursor: not-allowed; }

.spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ─── 手機版 ──────────────────────────────────── */
/* 排程時間欄位：在編輯頁面兩欄佈局下，內容區比視窗窄，需提早換行 */
@media (max-width: 1400px) {
  .mode-settings .form-row { grid-template-columns: 1fr; }
}

@media (max-width: 1024px) {
  .preview-sticky { display: none; }

  .editor-layout,
  .editor-tab-nav,
  .tracking-change-warning {
    margin-right: 0;
  }

  .editor-tab-nav {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding: 0 2px;
  }
  .editor-tab-btn {
    padding: 12px 16px;
    font-size: 13px;
  }

  .mobile-preview-toggle-bar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 12px;
  }
  .mobile-preview-toggle-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border: 1px solid #06C755;
    border-radius: 20px;
    background: #fff;
    color: #06C755;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
  }
  .mobile-preview-toggle-btn:hover { background: #f0fff5; }

  .mobile-preview-drawer {
    display: block;
    margin-bottom: 16px;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 16px rgba(0,0,0,.12);
    background: #fff;
  }
  .mobile-preview-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #f3f4f6;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
  }
  .mobile-preview-header i { color: #06C755; margin-right: 4px; }
  .mobile-preview-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 6px;
    background: #f3f4f6;
    color: #6b7280;
    cursor: pointer;
    font-size: 16px;
  }
  .mobile-preview-body { padding: 16px; }

  .form-row { grid-template-columns: 1fr; }
  /* 按鈕名稱 + LineActionField 需整欄寬度，否則「切換 Tab」等類型按鈕會被擠出可視區 */
  .header-actions { flex-wrap: wrap; }
}
</style>

<style>
.page-header-rich-menu-editor {
  position: fixed;
  top: 56px;
  left: 260px;
  right: 0;
  z-index: 100;
}
.sidebar.collapsed ~ .main-content .page-header-rich-menu-editor {
  left: 64px;
}
@media (max-width: 768px) {
  .page-header-rich-menu-editor {
    left: 0 !important;
    top: 56px;
  }
}
</style>
